import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Button } from "@/components/Button";
import { Field } from "@/components/Field";
import { type StoryPost, updatePost } from "@/lib/feedApi";

interface Props {
  post: StoryPost;
  onClose: () => void;
  onUpdated: (post: StoryPost) => void;
}

export function EditDialog({ post, onClose, onUpdated }: Props) {
  const [title, setTitle] = useState(post.title ?? "");
  const [body, setBody] = useState(post.body ?? post.excerpt ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitting, onClose]);

  async function save() {
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { post: updated } = await updatePost(post.id, { title, body });
      onUpdated(updated);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save your changes.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto py-10" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" onClick={() => !submitting && onClose()} className="fixed inset-0 bg-overlay backdrop-blur-[2px]" />
      <div className="relative w-full max-w-xl rounded-[var(--radius-lg)] border border-border-subtle bg-surface shadow-[var(--shadow-4)]">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3.5">
          <h2 className="text-sm font-semibold text-fg">Edit post</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={() => !submitting && onClose()}
            className="flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted hover:bg-surface-hover hover:text-fg [&_svg]:size-4"
          >
            <X />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-4">
          <Field.Root>
            <Field.Label optional>Title</Field.Label>
            <Field.Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a title" />
          </Field.Root>

          <Field.Root>
            <Field.Label>Update</Field.Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="What's happening on the ground?"
              className="w-full resize-y rounded-[var(--radius-md)] border border-border-default bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle hover:border-border-strong focus:outline-2 focus:outline-offset-2 focus:outline-border-focus"
            />
          </Field.Root>

          {error && <p className="text-sm text-danger-fg">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
          <Button intent="ghost" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button intent="primary" size="sm" onClick={save} disabled={!body.trim() || submitting}>
            {submitting && <Loader2 className="animate-spin" />}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
